// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { DocumentModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@sc/App/Components/Common/FancyScroll";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { getProductVideosAndDocumentsState } from "@sc/App/Store/Selectors/ProductSelectors";
import StyledBox, { StyledBoxStyles } from "@sc/XDL/Components/Common/Box";
import CAD from "@sc/XDL/Icons/CAD";
import DefaultDocument from "@sc/XDL/Icons/DefaultDocument";
import Excel from "@sc/XDL/Icons/Excel";
import PDF from "@sc/XDL/Icons/PDF";
import Word from "@sc/XDL/Icons/Word";
import * as React from "react";
import { css } from "styled-components";

// <XNG-Change>
const enum fields {
    showVideoDocuments = "showVideoDocuments",
}
interface Props extends WidgetProps {
    showTitle?: boolean;
    fields: {
        [fields.showVideoDocuments]: boolean;
    };
}

type OwnProps = Props & HasProduct;

export interface ProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    // XNG-Change
    title?: TypographyPresentationProps;
    documentWrapper?: InjectableCss;
    documentsWrapper?: StyledBoxStyles;
    linksWrapper?: InjectableCss;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            padding: 10px 15px 30px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
};

const styles = documentsStyles;

const ProductDetailsDocuments: React.FC<OwnProps> = ({
    // XNG-Change: rename documents, extract id, add title and fields.
    product: { documents: productDocuments, id: productId },
    fields: { showVideoDocuments },
}) => {
    // XNG-Change
    const documents = useAppSelector(state =>
        showVideoDocuments
            ? productDocuments
            : getProductVideosAndDocumentsState(state as CustomAppState, productId).others,
    );
    if (!documents || documents.length === 0) {
        return null;
    }

    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    // XNG-Change
    const getIcon = (document: DocumentModel) => {
        if (document.filePath.match(/\.xlsx?$/i)) {
            return Excel;
        }
        if (document.filePath.match(/\.docx?$/i)) {
            return Word;
        }
        if (document.filePath.match(/\.pdf$/i)) {
            return PDF;
        }
        if (document.filePath.match(/\.(dwg|dxf)$/i)) {
            return CAD;
        }
        return DefaultDocument;
    };
    // XNG-Change: changes on structure to match design
    return (
        <StyledBox extendedStyles={styles.documentsWrapper}>
            <Typography {...styles.title}>{translate("Documentation")}</Typography>
            <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef}>
                <StyledWrapper ref={parentBoxRef} {...styles.linksWrapper}>
                    {documents.map(document => (
                        <StyledWrapper key={document.id.toString()} {...styles.documentWrapper}>
                            <Link
                                href={document.filePath}
                                target="_new"
                                {...styles.documentLink}
                                data-test-selector={`productDetails_${document.id}`}
                                aria-label={document.name}
                            >
                                <IconMemo src={getIcon(document)} {...styles.documentIcon} />
                                <Typography {...styles.documentNameText}>{document.name}</Typography>
                            </Link>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </FancyScroll>
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDocuments),
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        // XNG-Change: Add flag to filter videos
        fieldDefinitions: [
            {
                name: fields.showVideoDocuments,
                displayName: "Show Video Documents",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
